import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import AuthLayout from '../../components/AuthLayout.jsx';

const FACILITY_TYPES = [
  { value: 'elderly', display: 'RCFE - Residential Care Facility for the Elderly' },
  { value: 'group', display: 'GH - Group Home' },
  { value: 'adult', display: 'ARF - Adult Residential Facility' },
  { value: 'short_term', display: 'STRTP - Short-Term Residential Therapeutic Program' },
];

export default function Register({ invitationToken, invitedEmail, invitedFirstName, invitedLastName, invitedStudentType }) {
  // For invited users, skip all selection steps and go straight to account details
  const isInvited = !!invitationToken;

  const [step, setStep] = useState(isInvited ? 'account' : 1);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const [userType, setUserType] = useState(isInvited ? 'student' : '');
  const [studentType, setStudentType] = useState(invitedStudentType || '');
  const [facilityType, setFacilityType] = useState('');
  const [trainingType, setTrainingType] = useState('');
  const [certificationType, setCertificationType] = useState('');
  const [email, setEmail] = useState(invitedEmail || '');
  const [firstName, setFirstName] = useState(invitedFirstName || '');
  const [lastName, setLastName] = useState(invitedLastName || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const isStudent = userType === 'student';
  const totalSteps = isStudent ? 5 : 2;

  const studentStep = step - 1; // 1=role, 2=facility, 3=training/cert, 4=account

  const canAdvance = () => {
    if (step === 1) return userType !== '';
    if (!isStudent) return false; // group manager goes straight to account form
    if (step === 2) return studentType !== '';
    if (step === 3) return facilityType !== '';
    if (step === 4) {
      if (studentType === 'Caregiver') return trainingType !== '';
      if (studentType === 'Administrator') return certificationType !== '';
      return false;
    }
    return false;
  };

  const handleNext = () => {
    if (!canAdvance()) return;

    if (step === 1 && !isStudent) {
      setStep(2);
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step === 2 && !isStudent) {
      setStep(1);
      return;
    }
    setStep(step - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setServerError(null);

    const userData = {
      user_type: userType,
      email,
      first_name: firstName,
      last_name: lastName,
      password,
      password_confirmation: passwordConfirmation,
    };

    if (isStudent) {
      userData.student_type = studentType;
      userData.facility_type_preference = facilityType;
      if (studentType === 'Caregiver') {
        userData.training_type = trainingType;
      } else {
        userData.certification_type = certificationType;
      }
    }

    Object.keys(userData).forEach(key => {
      if (userData[key] === '') userData[key] = null;
    });

    router.post('/register', {
      user: userData,
      invitation_token: invitationToken || '',
    }, {
      onError: (errors) => {
        setServerError(Object.values(errors).flat().join(', '));
        setSubmitting(false);
      },
      onFinish: () => setSubmitting(false),
    });
  };

  const isAccountStep = step === 'account' || (isStudent && step === 5) || (!isStudent && step === 2);

  const stepLabels = isStudent
    ? ['Account Type', 'Your Role', 'Facility Type', studentType === 'Caregiver' ? 'Training Type' : 'Certification Type', 'Account Details']
    : ['Account Type', 'Account Details'];

  const inputClass = "mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6";

  const radioCardClass = (selected) =>
    `flex items-center cursor-pointer p-4 border-2 rounded-lg transition-colors ${selected ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}`;

  return (
    <AuthLayout>
      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        {isInvited && (
          <div className="mb-6 sm:mx-auto sm:w-full sm:max-w-md bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">Set a password to complete your account and accept the facility invitation.</p>
          </div>
        )}

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="123CEU" className="h-12 w-auto" />
          </div>
          <h2 className="mt-2 text-center text-2xl/9 font-bold tracking-tight text-gray-900">Create your account</h2>
          <p className="mt-2 text-center text-sm/6 text-gray-600">
            Already have an account?{' '}
            <Link
              href={`/login${invitationToken ? `?invitation_token=${invitationToken}${invitedEmail ? `&email=${encodeURIComponent(invitedEmail)}` : ''}` : ''}`}
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              Sign in
            </Link>
          </p>

          {/* Progress indicator - hidden for invited users (single step) */}
          {userType && !isInvited && (
            <div className="mt-6 flex items-center gap-2">
              {stepLabels.map((label, i) => (
                <div key={i} className="flex-1">
                  <div className={`h-1.5 rounded-full ${i + 1 <= step ? 'bg-indigo-600' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          {serverError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{serverError}</p>
            </div>
          )}

          {/* Step 1: Account Type */}
          {step === 1 && (
            <div className="space-y-4">
              <label className="block text-sm/6 font-medium text-gray-900">I'm registering as a:</label>
              <fieldset className="space-y-3">
                <label className={radioCardClass(userType === 'student')}>
                  <input type="radio" name="user_type" value="student" checked={userType === 'student'}
                    onChange={() => { setUserType('student'); setStudentType(''); setFacilityType(''); setTrainingType(''); setCertificationType(''); }}
                    className="h-4 w-4 text-indigo-600" />
                  <span className="ml-3">
                    <span className="font-medium text-gray-900">Student</span>
                    <span className="block text-sm text-gray-500">Individual user</span>
                  </span>
                </label>
                <label className={radioCardClass(userType === 'group_manager')}>
                  <input type="radio" name="user_type" value="group_manager" checked={userType === 'group_manager'}
                    onChange={() => setUserType('group_manager')}
                    className="h-4 w-4 text-indigo-600" />
                  <span className="ml-3">
                    <span className="font-medium text-gray-900">Group Manager</span>
                    <span className="block text-sm text-gray-500">Managing multiple users</span>
                  </span>
                </label>
              </fieldset>

              <button type="button" onClick={handleNext} disabled={!canAdvance()}
                className="mt-6 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                Continue
              </button>
            </div>
          )}

          {/* Step 2 (Student): Your Role */}
          {isStudent && step === 2 && (
            <div className="space-y-4">
              <label className="block text-sm/6 font-medium text-gray-900">Your Role</label>
              <fieldset className="space-y-3">
                <label className={radioCardClass(studentType === 'Caregiver')}>
                  <input type="radio" name="student_type" value="Caregiver" checked={studentType === 'Caregiver'}
                    onChange={() => { setStudentType('Caregiver'); setCertificationType(''); }}
                    className="h-4 w-4 text-indigo-600" />
                  <span className="ml-3">
                    <span className="font-medium text-gray-900">Caregiver / Direct Care Staff</span>
                  </span>
                </label>
                <label className={radioCardClass(studentType === 'Administrator')}>
                  <input type="radio" name="student_type" value="Administrator" checked={studentType === 'Administrator'}
                    onChange={() => { setStudentType('Administrator'); setTrainingType(''); }}
                    className="h-4 w-4 text-indigo-600" />
                  <span className="ml-3">
                    <span className="font-medium text-gray-900">Facility Administrator</span>
                  </span>
                </label>
              </fieldset>

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={handleBack}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Back
                </button>
                <button type="button" onClick={handleNext} disabled={!canAdvance()}
                  className="flex-1 rounded-md bg-indigo-600 px-3 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3 (Student): Facility Type */}
          {isStudent && step === 3 && (
            <div className="space-y-4">
              <label className="block text-sm/6 font-medium text-gray-900">Facility Type</label>
              <fieldset className="space-y-3">
                {FACILITY_TYPES.map((ft) => (
                  <label key={ft.value} className={radioCardClass(facilityType === ft.value)}>
                    <input type="radio" name="facility_type" value={ft.value} checked={facilityType === ft.value}
                      onChange={() => setFacilityType(ft.value)}
                      className="h-4 w-4 text-indigo-600" />
                    <span className="ml-3 font-medium text-gray-900">{ft.display}</span>
                  </label>
                ))}
              </fieldset>

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={handleBack}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Back
                </button>
                <button type="button" onClick={handleNext} disabled={!canAdvance()}
                  className="flex-1 rounded-md bg-indigo-600 px-3 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 4 (Student): Training Type or Certification Type */}
          {isStudent && step === 4 && (
            <div className="space-y-4">
              {studentType === 'Caregiver' ? (
                <>
                  <label className="block text-sm/6 font-medium text-gray-900">Training Type</label>
                  <fieldset className="space-y-3">
                    <label className={radioCardClass(trainingType === 'initial_training')}>
                      <input type="radio" name="training_type" value="initial_training" checked={trainingType === 'initial_training'}
                        onChange={() => setTrainingType('initial_training')}
                        className="h-4 w-4 text-indigo-600" />
                      <span className="ml-3 font-medium text-gray-900">Initial Training</span>
                    </label>
                    <label className={radioCardClass(trainingType === 'continuing_education')}>
                      <input type="radio" name="training_type" value="continuing_education" checked={trainingType === 'continuing_education'}
                        onChange={() => setTrainingType('continuing_education')}
                        className="h-4 w-4 text-indigo-600" />
                      <span className="ml-3 font-medium text-gray-900">Annual Training</span>
                    </label>
                  </fieldset>
                </>
              ) : (
                <>
                  <label className="block text-sm/6 font-medium text-gray-900">Certification Type</label>
                  <fieldset className="space-y-3">
                    <label className={radioCardClass(certificationType === 'recertification')}>
                      <input type="radio" name="certification_type" value="recertification" checked={certificationType === 'recertification'}
                        onChange={() => setCertificationType('recertification')}
                        className="h-4 w-4 text-indigo-600" />
                      <span className="ml-3 font-medium text-gray-900">Recertification - CETP</span>
                    </label>
                  </fieldset>
                </>
              )}

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={handleBack}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Back
                </button>
                <button type="button" onClick={handleNext} disabled={!canAdvance()}
                  className="flex-1 rounded-md bg-indigo-600 px-3 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Account Details (final step for both flows) */}
          {isAccountStep && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">Email</label>
                <input id="email" type="email" required autoComplete="email" placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  readOnly={isInvited} className={`${inputClass}${isInvited ? ' bg-gray-50 text-gray-500' : ''}`} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm/6 font-medium text-gray-900">First Name</label>
                  <input id="first_name" type="text" required autoComplete="given-name" placeholder="John"
                    value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm/6 font-medium text-gray-900">Last Name</label>
                  <input id="last_name" type="text" required autoComplete="family-name" placeholder="Doe"
                    value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">Password</label>
                <p className="text-xs text-gray-500">Minimum 8 characters</p>
                <input id="password" type="password" required autoFocus={isInvited} autoComplete="new-password" placeholder="Enter a secure password"
                  maxLength={72} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
              </div>

              <div>
                <label htmlFor="password_confirmation" className="block text-sm/6 font-medium text-gray-900">Confirm Password</label>
                <input id="password_confirmation" type="password" required autoComplete="new-password" placeholder="Confirm your password"
                  maxLength={72} value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} className={inputClass} />
              </div>

              <div className="flex gap-3 pt-2">
                {!isInvited && (
                  <button type="button" onClick={handleBack}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
                    Back
                  </button>
                )}
                <button type="submit" disabled={submitting}
                  className="flex-1 rounded-md bg-indigo-600 px-3 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-50">
                  {submitting ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
